import { Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { memo } from 'react';

import { AnalyticsGranularity } from 'shared/api';

const GRANULARITY_OPTIONS: Array<{ value: AnalyticsGranularity; label: string }> = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'half-year', label: 'Half-year' },
];

type Props = {
  from: Date | null;
  to: Date | null;
  granularity: AnalyticsGranularity;
  onFromChange: (date: Date | null) => void;
  onToChange: (date: Date | null) => void;
  onGranularityChange: (g: AnalyticsGranularity) => void;
};

const formatRange = (from: Date, to: Date): string => {
  const f = (d: Date) => d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${f(from)} — ${f(to)}`;
};

export const AnalyticsToolbar = memo(
  ({ from, to, granularity, onFromChange, onToChange, onGranularityChange }: Props) => (
    <Paper sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" p="8px 16px" gap={2} flexWrap="wrap">
        <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
          Analytics
        </Typography>

        <Box display="flex" alignItems="center" gap={1.5}>
          <DatePicker
            label="From"
            value={from}
            onChange={onFromChange}
            maxDate={to ?? undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
            format="dd MMM yyyy"
          />
          <DatePicker
            label="To"
            value={to}
            onChange={onToChange}
            minDate={from ?? undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
            format="dd MMM yyyy"
          />
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={granularity}
          onChange={(_, v) => v && onGranularityChange(v as AnalyticsGranularity)}
        >
          {GRANULARITY_OPTIONS.map(({ value, label }) => (
            <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 2 }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ ml: 'auto' }}>
          <Typography variant="body2" color="text.secondary">
            {from && to ? formatRange(from, to) : 'Last 6 months'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  ),
);

AnalyticsToolbar.displayName = 'AnalyticsToolbar';
